/**
 * JSON representation of a Contact from the server
 {
    id:           '77f6d9f8-f5b4-4bd9-a596-378979a125af',
    student_id:   '51db4bd8e9c77f81290001ec',
    name:         'Aaronson, Able T',
    phone:        '(Home) 601-878-4185',
    email:        'levi@schoolstatus.com',
    relationship: 'Tester',
    resides_with: null,
    checkout:     null,
    emergency:    null,
    no_contact:   null,
    primary:      true,
    user_flagged: true,
    stopped:      false,
    student:      { full_name: 'John Brown' },
    flagged:      true,
    flagged_count: 4,
    flags: [{
      id: '12',
      note: 'I just don't like the guy',
      created_at: '2018-06-05T16:54:41.136-05:00',
      user: {
        name: 'Ableseed, Jacob T',
        phone: '(Home) 601-878-4185',
      }
    },{
      id: '14',
      note: 'I just don't like the guy',
      created_at: '2018-06-05T16:54:41.136-05:00',
      user: {
        name: 'Aaronson, Able T',
        phone: '(Home) 601-878-4185',
      }
    }]
  }
*/

import { observable, action, computed, autorun } from 'mobx'

import { setter }                                from 'mobx-decorators'
import _                                         from 'lodash'

import xhr                                       from 'helpers/XHR'
import fireEvent                                 from 'helpers/FireEvent'
import getError                                  from 'helpers/ErrorParser'

import uiStore                                   from 'stores/UiStore'
import rootCallingStore                          from 'stores/RootCallingStore'
import smsInboxStore                             from 'stores/SMSInboxStore'

import Flag                                      from 'stores/models/Flag'

export default class Contact {
  id = null
  parentStore = null

  @setter @observable studentId = null
  @setter @observable student = null
  @setter @observable name = null
  @setter @observable note = ''
  @setter @observable phone = null
  @setter @observable email = null
  @setter @observable relationship = null
  @setter @observable residesWith = null
  @setter @observable checkout = null
  @setter @observable emergency = null
  @setter @observable noContact = null
  @setter @observable phoneType = null
  @setter @observable avatarUrl = null
  @setter @observable userFlagged = false
  @setter @observable preferredLanguage = null

  @setter @observable flagsCount = 0
  @setter @observable flags = []
  @setter @observable flagged = false

  @setter @observable isError = false
  @setter @observable isFlagging = false
  @setter @observable isUnFlagging = false
  @setter @observable isFetchingNumCapability = false
  @setter @observable isUpdatingContactPrimary = false
  @setter @observable isUpdatingPreferredLanguage = false
  @setter @observable isUpdatingEmailPrimary = false

  @setter @observable isEmailPrimary = false
  @setter @observable primary = false
  @setter @observable stopped = false

  @setter @observable phoneFormatted = null
  @setter @observable phoneNote = null

  constructor(store, json) {
    this.parentStore = store

    this.updateFromJSON(json)

    this.initAutoruns()
  }

  // Computed
  @computed get isTextingDisabled() {
    return (
      !_.includes(['mobile', 'voip'], this.phoneType) || this.stopped
    )
  }

  @computed get hasFlags() {
    return !_.isEmpty(this.flags)
  }

  @computed get dataSource() {
    return _.orderBy(
      this.flags,
      (f) => f.createdAt,
      'desc'
    ).map((f) => ({ ...f.toJS, key: f.id }))
  }

  @computed get showContactFlagging() {
    return true
  }

  @computed get canUnflag() {
    return !this.isUnFlagging && this.userFlagged
  }

  // Actions
  @action updateFromJSON = ({
    id,
    student_id: studentId,
    name,
    phone,
    phone_hash: phoneHash,
    email,
    relationship,
    resides_with: residesWith,
    checkout,
    emergency,
    no_contact: noContact,
    primary,
    stopped,
    student,
    flagged,
    flags_count: flagsCount,
    note = '',
    flags = [],
    avatarUrl: avatarUrl,
    user_flagged: userFlagged,
    phone_type: phoneType,
    primary_email: primaryEmail,
    preferred_language: preferredLanguage
  }) => {
    this.id = id
    this.studentId = studentId
    this.name = name || 'N/A'
    this.phone = phone
    this.email = email
    this.relationship = relationship
    this.residesWith = residesWith
    this.checkout = checkout
    this.emergency = emergency
    this.noContact = noContact
    this.primary = primary
    this.isEmailPrimary = primaryEmail
    this.stopped = stopped
    this.student = student
    this.note = note
    this.avatarUrl = avatarUrl
    this.userFlagged = userFlagged

    this.flagsCount = flagsCount
    this.flagged = flagged

    this.phoneType = phoneType
    this.phoneFormatted = phoneHash
      ? phoneHash['formatted_number']
      : null
    this.phoneNote = phoneHash ? phoneHash['parsed_note'] : null

    this.flags = flags.map(this.createFlag)
    this.setPreferredLanguage(
      preferredLanguage ? preferredLanguage : null
    )
  }

  // Services
  @action flagInvalidNumber = async(reasonStore) => {
    try {
      this.setIsFlagging(true)
      this.setIsError(false)

      const res = await xhr.put(`/contacts/${this.id}/flag`, {
        flag:   !this.flagged,
        note:   this.note,
        reason: reasonStore.selectedReason.id
      })

      this.flagInvalidNumberOK(res)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsFlagging(false)
    }
  }

  @action flagInvalidNumberOK = (res) => {
    uiStore.addMessage(`Contact flagged successfully`)

    this.updateFromJSON(res.data)
    this.parentStore.toggleFlagFormModal()
  }

  @action updateContactPrimary = async() => {
    try {
      this.setIsUpdatingContactPrimary(true)
      this.setIsError(false)

      const res = await xhr.put(`/contacts/${this.id}/primary`, {
        primary: !this.primary
      })

      this.updateContactPrimaryOK(res)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsUpdatingContactPrimary(false)
    }
  }

  @action updateContactPrimaryOK = (res) => {
    this.updateFromJSON(res.data)
    uiStore.addMessage(
      res.data.primary
        ? 'Contact number is now preferred'
        : 'Contact number no longer preferred'
    )
  }

  @action updateEmailPrimary = async() => {
    try {
      this.setIsUpdatingEmailPrimary(true)
      this.setIsError(false)

      const res = await xhr.put(
        `/contacts/${this.id}/primary_email`,
        {
          primary: !this.isEmailPrimary
        }
      )

      this.updateEmailPrimaryOK(res)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsUpdatingEmailPrimary(false)
    }
  }

  @action updateEmailPrimaryOK = (res) => {
    this.updateFromJSON(res.data)
    uiStore.addMessage(
      res.data.primary_email
        ? 'Contact email is now preferred'
        : 'Contact email no longer preferred'
    )
  }

  @action createFlag = (flag) => {
    return new Flag(this, flag)
  }

  // Autoruns
  initAutoruns = () => {
    this.autoErrorNotifier()
  }

  @action autoErrorNotifier = () => {
    this.autoErrorDisposer = autorun('Watch errors', () => {
      if (this.isError && !this.isError.hideNotification) {
        uiStore.addNotification({
          title:   this.isError.title,
          message: this.isError.message,
          type:    'error'
        })
      }
    })
  }

  @action dispose = () => {
    this.autoErrorDisposer && this.autoErrorDisposer()
  }

  @action clearErrors = () => {
    this.setIsError(false)
  }

  @action initiateCall = () => {
    rootCallingStore.callingStore.setShowCallDialog(true)
    rootCallingStore.callingStore.contact = this
    rootCallingStore.callingStore.studentID = this.studentId

  }

  @action initiateText = () => {
    fireEvent('toggleSidebar', { contact: this })
  }

  @action initiateEmail = () => {
    fireEvent('showMailer', {
      type:              'individual',
      id:                this.id,
      name:              this.name,
      preferredLanguage: this.preferredLanguage
    })
  }

  @action initiateVideo = () => {
    fireEvent('startVideoMessage', { contact: this })
  }

  @action unFlagNumber = async() => {
    try {
      this.setIsUnFlagging(true)
      this.setIsError(false)

      const { data } = await xhr.delete(`/contacts/${this.id}/flag`)

      this.unFlagNumberOK(data)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsUnFlagging(false)
    }
  }

  @action unFlagNumberOK = (contact) => {
    this.updateFromJSON(contact)
    uiStore.addMessage(`flag removed successfully`)
  }

  @action updatePreferredLanguage = async(preferredLanguage) => {
    try {
      this.setIsUpdatingPreferredLanguage(true)

      const { data } = await xhr.put(
        `/contacts/${this.id}/preferred_language`,
        {
          preferred_language: preferredLanguage
        }
      )

      this.updatePreferredLanguageOk(data)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsUpdatingPreferredLanguage(false)
    }
  }

  @action updatePreferredLanguageOk = (contact) => {
    this.updateFromJSON(contact)
    smsInboxStore.updateContactLanguage(contact)
  }
}
